import React, { useState } from 'react';
import api from '../../services/api';
import { useToast } from '../shared/ToastNotification';

interface QuickRegistrationProps {
    initialName?: string;
    onSuccess: (customer: any) => void;
    onCancel: () => void;
}

const QuickRegistration: React.FC<QuickRegistrationProps> = ({ 
    initialName = '', 
    onSuccess, 
    onCancel 
}) => {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        name: initialName,
        phone: '',
        email: ''
    });
    const [errors, setErrors] = useState<string[]>([]);

    const handleCreateCustomer = async () => {
        if (!newCustomer.name.trim()) {
            toast.error('Name is required');
            return;
        }

        setLoading(true);
        setErrors([]);

        try {
            // Using the same endpoint as InvoiceForm's "Quick Setup"
            const response = await api.post('/search/quick_create_customer', { 
                name: newCustomer.name,
                phone: newCustomer.phone,
                email: newCustomer.email
            });

            if (response.success) {
                // The standardized API response puts data in 'data'
                // SearchController#quick_create_customer returns { customer: ... }
                const customerData = response.data?.customer || response.data;
                
                toast.success('Customer registered successfully!');
                onSuccess(customerData);
            } else {
                const backendErrors = response.errors || [response.message || 'Failed to create customer'];
                setErrors(Array.isArray(backendErrors) ? backendErrors : [backendErrors]);
                toast.error(backendErrors[0]);
            }
        } catch (err: any) {
            console.error('Registration failed:', err);
            toast.error('An unexpected error occurred during registration');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-slate-50 border border-indigo-100 rounded-2xl space-y-4 animate-in slide-in-from-top-2 duration-300 shadow-inner shadow-slate-200/50">
            <h4 className="text-xs font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                Rapid Registration
            </h4>
            
            {errors.length > 0 && (
                <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg text-rose-600 text-[10px] font-bold">
                    {errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
            )}

            <div className="grid grid-cols-1 gap-3">
                <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm"
                    placeholder="Legal Name"
                    disabled={loading}
                    autoFocus
                />
                <div className="grid grid-cols-2 gap-3">
                    <input
                        type="tel"
                        value={newCustomer.phone}
                        onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm"
                        placeholder="Mobile Number"
                        disabled={loading}
                    />
                    <input
                        type="text"
                        value={newCustomer.gst_number}
                        onChange={(e) => setNewCustomer(prev => ({ ...prev, gst_number: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm uppercase"
                        placeholder="GST Number (Optional)"
                        disabled={loading}
                    />
                </div>
                <input
                    type="text"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm"
                    placeholder="Full Address"
                    disabled={loading}
                />
                <input
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm"
                    placeholder="Email Address"
                    disabled={loading}
                />
            </div>

            <div className="flex gap-2 pt-2">
                <button
                    type="button"
                    onClick={handleCreateCustomer}
                    disabled={loading}
                    className="flex-1 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 active:scale-[0.98] disabled:opacity-50"
                >
                    {loading ? 'Processing...' : 'Confirm Registration'}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={loading}
                    className="px-6 py-3 bg-white border border-slate-200 text-slate-500 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default QuickRegistration;
